/** @jsxImportSource @emotion/react */

import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme, TIconSize, TTone } from '@datatype/type';

type TComponentSize = 'small' | 'medium' | 'large';
type TKind = 'filled' | 'outlined';
type TShape = 'round' | 'square';

interface IProps {
  theme?: ETheme;
  tone?: TTone;
  kind?: TKind;
  size?: TComponentSize;
  shape?: TShape;
  width?: string;
  height?: string;
  sources: string[];
  timing?: {
    begin?: number;
    gap?: number;
    end?: number;
  };
}

interface IAudioSourceProps {
  index: number;
  src: string;
  audioRefs: React.MutableRefObject<HTMLAudioElement[]>;
  handlePlayCompleted: () => Promise<void>;
}

const Wrapper = styled.div<{ $width: string; $height: string }>`
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
`;

const AudioSource: React.FC<IAudioSourceProps> = ({ index, src, audioRefs, handlePlayCompleted }) => {
  const elementRef = useRef<HTMLAudioElement | null>(null);
  const handleRef = useCallback(
    (element: HTMLAudioElement | null) => {
      if (!element && elementRef.current) {
        elementRef.current.pause();
        elementRef.current.currentTime = 0;
      }

      elementRef.current = element;
      if (element) {
        audioRefs.current[index] = element;
      }
    },
    [audioRefs, index],
  );

  return <audio ref={handleRef} src={src} onEnded={handlePlayCompleted} controls={false} />;
};

const createDesign = (
  theme: ETheme,
  tone: TTone,
  btnSize: TComponentSize,
  kind: TKind,
  shape: TShape,
  height: string,
  width: string,
) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (btnSize) {
      case 'small':
        return {
          boxHeight: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
          boxWidth: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
          iconSize: 'small' as TIconSize,
        };
      case 'large':
        return {
          boxHeight: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
          boxWidth: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
          iconSize: 'large' as TIconSize,
        };
      case 'medium':
      default:
        return {
          boxHeight: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
          boxWidth: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
          iconSize: 'medium' as TIconSize,
        };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    const createThemeColor = (theme: ETheme, tone: TTone): string => {
      if (theme === ETheme.BROWN) {
        if (tone === 'light') return color.BROWN_3;
        if (tone === 'normal') return color.BROWN_2;
        return color.BROWN_1;
      }
      if (theme === ETheme.GRAY) {
        if (tone === 'light') return color.GRAY_2;
        if (tone === 'normal') return color.GRAY_1;
        return color.BLACK_4;
      }
      return color.BLACK_2;
    };

    const themeColor = createThemeColor(theme, tone);
    if (kind === 'outlined') {
      return { backgroundColor: 'transparent', iconColor: themeColor, borderColor: themeColor };
    }
    return { backgroundColor: themeColor, iconColor: color.WHITE_1, borderColor: themeColor };
  };

  // ############### createEvent ###############
  const createEvent = () => ({ cursor: 'pointer' as const });

  // ############### Combine ###############
  const sizeDesign = createSize();
  const themeDesign = createTheme();

  const boxDesign = {
    backgroundColor: themeDesign.backgroundColor,
    borderRadius: shape === 'round' ? size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS : '0px',
    event: createEvent(),
    width: width === 'fit-content' ? sizeDesign.boxWidth : width,
    height: height === 'auto' ? sizeDesign.boxHeight : height,
    border: kind === 'outlined',
    borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
    borderColor: themeDesign.borderColor,
  } as const;

  const iconDesign = {
    size: sizeDesign.iconSize,
    kind,
    color: themeDesign.iconColor,
  } as const;

  return { box: boxDesign, icon: iconDesign };
};

const ImageAudioButton: React.FC<IProps> = ({
  theme = ETheme.BROWN,
  tone = 'strong',
  kind = 'filled',
  size = 'medium',
  shape = 'square',
  width = 'fit-content',
  height = 'auto',
  sources,
  timing = {},
}) => {
  const design = createDesign(theme, tone, size, kind, shape, height, width);
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick } = useAudioPlayer({ sources, timing });

  return (
    <Wrapper $width={width} $height={height}>
      <IconBlock
        icon={isPlaying ? 'volume-off' : 'volume-on'}
        design={{ box: design.box, icon: design.icon }}
        click={{ handler: handlePlayAndPauseClick }}
      />
      {sources &&
        sources.map((src, idx) => (
          <AudioSource
            key={`${idx}-${src}`}
            index={idx}
            src={src}
            audioRefs={audioRefs}
            handlePlayCompleted={handlePlayCompleted}
          />
        ))}
    </Wrapper>
  );
};

export default ImageAudioButton;
